/* eslint-disable react/require-default-props */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FaExclamationTriangle } from 'react-icons/fa';

import EventConfirmDialog from '../../RouteManagement/components/EventConfirmDialog';
import { recordItemEvent } from '../../../../services/routing/routingScheduleApi';

// =============================================================================
// Aviso de intermediários faltantes (M3 §4).
//
// A ficha de um item REALIZADO traz missing_intermediate_events: quais dos
// checkpoints OTW/ARRIVED/ON_SERVICE NÃO aparecem entre os eventos ativos
// (vazio se o fluxo foi completo, se o item não é REALIZADO, ou na listagem).
//
// É ORIENTAÇÃO, não bloqueio — nunca impede marcar/manter REALIZADO. Quem tem
// routing:schedule:record_event OU routing:finance:operate pode inserir os
// faltantes pelo POST de events (mesmo dialog do board). Marcar terminal "cedo"
// gera sintéticos que não contam como intermediários — daí o aviso (M2 §3-4).
// =============================================================================

const EVENT_LABELS = {
   OTW: 'A caminho',
   ARRIVED: 'Cheguei',
   ON_SERVICE: 'Em atendimento',
};

const Banner = styled.div`
   display: flex;
   flex-direction: column;
   gap: 10px;
   background: rgba(245, 158, 11, 0.08);
   border: 1px solid rgba(245, 158, 11, 0.5);
   border-radius: 10px;
   padding: 12px 14px;
   margin-bottom: 12px;
   font-size: 0.82rem;
   color: #92400e;
   line-height: 1.5;
`;

const Title = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   font-weight: 600;
`;

const ChipRow = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 8px;
`;

const InsertButton = styled.button`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   background: #fff;
   border: 1px solid rgba(245, 158, 11, 0.6);
   color: #92400e;
   border-radius: 8px;
   padding: 5px 12px;
   font-size: 0.78rem;
   font-weight: 600;
   cursor: pointer;
   transition: background 120ms ease;

   &:hover:not(:disabled) {
      background: rgba(245, 158, 11, 0.12);
   }

   &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
   }
`;

export default function MissingIntermediatesPanel({
   item,
   canRecordEvent,
   onInserted,
}) {
   // kindDef em registro ({ kind, label }) — controla o EventConfirmDialog.
   const [pickedKind, setPickedKind] = useState(null);

   const missing = Array.isArray(item?.missing_intermediate_events)
      ? item.missing_intermediate_events
      : [];

   // Só faz sentido em item REALIZADO com checkpoints faltando. O backend já
   // devolve [] fora desses casos, mas reforçamos o gate aqui.
   if (item?.item_status !== 'REALIZADO' || missing.length === 0) return null;

   const handleSubmit = async (payload) => {
      // recordItemEvent lança em erro — o EventConfirmDialog captura e mostra
      // os erros (por campo / geral). No sucesso, refazemos o fetch do item:
      // o checkpoint inserido some da lista de faltantes.
      await recordItemEvent(
         item.schedule_date,
         item.schedule_period,
         item.id,
         payload
      );
      toast.success('Checkpoint registrado.');
      setPickedKind(null);
      onInserted?.();
   };

   const labels = missing.map((kind) => EVENT_LABELS[kind] ?? kind).join(', ');

   return (
      <>
         <Banner>
            <Title>
               <FaExclamationTriangle size={13} />
               Checkpoints não registrados: {labels}
            </Title>
            <span>
               Este item foi marcado como Realizado sem passar por todos os
               checkpoints intermediários. É só uma orientação — não bloqueia o
               status. {canRecordEvent && 'Insira os faltantes se desejar:'}
            </span>
            {canRecordEvent && (
               <ChipRow>
                  {missing.map((kind) => (
                     <InsertButton
                        key={kind}
                        type="button"
                        onClick={() =>
                           setPickedKind({
                              kind,
                              label: EVENT_LABELS[kind] ?? kind,
                           })
                        }
                     >
                        + {EVENT_LABELS[kind] ?? kind}
                     </InsertButton>
                  ))}
               </ChipRow>
            )}
         </Banner>

         <EventConfirmDialog
            isOpen={Boolean(pickedKind)}
            onClose={() => setPickedKind(null)}
            stop={{
               nm: item.customer_name,
               id: item.order_number ?? item.id,
            }}
            kindDef={pickedKind}
            isTerminalOverride={false}
            onSubmit={handleSubmit}
         />
      </>
   );
}

MissingIntermediatesPanel.propTypes = {
   item: PropTypes.object,
   canRecordEvent: PropTypes.bool,
   onInserted: PropTypes.func,
};
